import React, { useState } from "react";
import { Card, bboxShort } from "./primitives";
import { api } from "@/lib/api";

const cropSrc = (cid, c, dpi) => `${api.defaults.baseURL}/collaudo-lettore/cases/${cid}/crop?page=${c.page || 1}&x0=${c.bbox_pct.x0}&y0=${c.bbox_pct.y0}&x1=${c.bbox_pct.x1}&y1=${c.bbox_pct.y1}&dpi=${dpi || c.dpi || 220}&pad=0.3`;

const Measure = ({ m }) => {
  if (!m) return null;
  const skip = new Set(["method", "conclusion", "numeric_words_on_page", "plate_edges_x_pt", "slot_straight_edges_x_pt", "slot_straight_span_y_pt", "dim55_extension_lines_y_pt", "image_px", "occurrences_of_2B"]);
  return (
    <div className="border border-border rounded-sm p-2 bg-ivory text-[10px] space-y-1">
      <div><b>Misura oggettiva</b> — {m.method}</div>
      <div className="grid gap-x-4 gap-y-0.5 sm:grid-cols-2 font-mono">
        {Object.entries(m).filter(([k]) => !skip.has(k)).map(([k, v]) => <div key={k}><span className="text-muted-foreground">{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}</div>)}
      </div>
      {m.conclusion && <div className="font-bold">{m.conclusion}</div>}
    </div>
  );
};

const Hypothesis = ({ h }) => (
  <div className="border border-border rounded-sm p-2 text-[10px]">
    <div className="font-bold">Ipotesi {h.id}: {h.text}</div>
    {h.supported_by?.length > 0 && <div className="text-ok-800">A favore: {h.supported_by.join(" · ")}</div>}
    {h.contradicted_by?.length > 0 && <div className="text-risk-800">Contro: {h.contradicted_by.join(" · ")}</div>}
    {!h.supported_by?.length && !h.contradicted_by?.length && <div className="text-muted-foreground">Nessun elemento a favore o contro registrato.</div>}
  </div>
);

const Check = ({ c }) => {
  const [big, setBig] = useState(null);
  return (
    <details className="border border-border rounded-sm" open data-testid={`cl-owner-check-${c.check_id}`}>
      <summary className="px-2 py-1 cursor-pointer text-[11px] bg-muted flex flex-wrap gap-x-3">
        <b>{c.check_id} · {c.case_id} · {c.field}</b><span>{c.title}</span>
        <span className="px-1.5 border border-warn-300 bg-warn-50 text-warn-800 font-bold" data-testid={`cl-owner-check-status-${c.check_id}`}>{c.status.replaceAll("_", " ")}</span>
      </summary>
      <div className="p-2 space-y-2">
        <div className="grid gap-1 sm:grid-cols-3 text-[10px]">
          <div><span className="text-muted-foreground">FASE A:</span> {c.fase_a_value}</div>
          <div><span className="text-muted-foreground">Primo risultato:</span> {c.reader_first}</div>
          <div><span className="text-muted-foreground">REV1:</span> {c.reader_rev1}</div>
        </div>
        <div className="grid gap-2 lg:grid-cols-[2fr_3fr]">
          <figure className="border border-border rounded-sm p-1 bg-white">
            <img src={cropSrc(c.case_id, c.context_crop, 140)} alt={c.context_crop.label} className="w-full cursor-zoom-in" onClick={() => setBig(c.context_crop)} data-testid={`cl-owner-check-context-${c.check_id}`} />
            <figcaption className="text-[10px] text-muted-foreground mt-1">Contesto — {c.context_crop.label} · p.{c.context_crop.page} {bboxShort(c.context_crop.bbox_pct)}</figcaption>
          </figure>
          <div className="grid gap-2 sm:grid-cols-2">
            {c.detail_crops.map((d, i) => (
              <figure key={i} className="border border-border rounded-sm p-1 bg-white">
                <img src={cropSrc(c.case_id, d, Math.min(d.dpi || 300, 600))} alt={d.label} className="w-full cursor-zoom-in" onClick={() => setBig(d)} data-testid={`cl-owner-check-detail-${c.check_id}-${i}`} />
                <figcaption className="text-[10px] text-muted-foreground mt-1">{i + 1}. {d.label} · p.{d.page} {bboxShort(d.bbox_pct)}</figcaption>
              </figure>
            ))}
          </div>
        </div>
        <Measure m={c.geometric_measurement} />
        <div className="grid gap-2 sm:grid-cols-2">{c.hypotheses.map((h) => <Hypothesis key={h.id} h={h} />)}</div>
        {c.proposed_rectification && (
          <div className="text-[10px] border border-dashed border-border rounded-sm p-2" data-testid={`cl-owner-check-rectification-${c.check_id}`}>
            <b>Bozza di rettifica (append-only, NON applicata):</b> {c.proposed_rectification.campo}: «{c.proposed_rectification.fase_a_value}» → «{c.proposed_rectification.proposed_value}» · base: {c.proposed_rectification.basis} · decisa da: {c.proposed_rectification.decided_by || "— (nessuna decisione registrata)"}
          </div>
        )}
        <div className="text-[10px] text-muted-foreground">{c.note}</div>
        {big && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setBig(null)} data-testid="cl-owner-check-zoom">
            <img src={cropSrc(c.case_id, big, Math.min(big.dpi || 300, 600))} alt={big.label} className="max-h-full max-w-full bg-white" />
          </div>
        )}
      </div>
    </details>
  );
};

/** Verifiche tecniche preparate per l'Owner: ritagli leggibili, misure oggettive, ipotesi; nessuna decisione registrata. */
export const OwnerChecksSection = ({ checks, rectifications }) => {
  const list = checks?.checks || [];
  const rects = rectifications?.rectifications || [];
  return (
    <Card title={`Verifiche tecniche dell'Owner (${list.length}) — in attesa di conferma`} subtitle={checks?.rule || "Nessuna verifica preparata."} testId="cl-owner-checks" tone="border-warn-300">
      {list.map((c) => <Check key={c.check_id} c={c} />)}
      <div className="text-[10px]" data-testid="cl-rectifications">
        <b>Registro rettifiche FASE A (append-only):</b> {rects.length === 0 ? "nessuna rettifica registrata — la FASE A sigillata è immutata" : rects.map((r, i) => <span key={i}>{r.case_id}/{r.campo}: {r.fase_a_value} → {r.rectified_value} ({r.decided_by}, {r.decided_at}); </span>)}
      </div>
    </Card>
  );
};
